<template>
    <div class="chat-group-member">
        <div class="member-card-container">
            <div class="header">
                <div class="header-content">
                    <i class="el-icon-back back-btn" @click="onBack()" title="返回"></i>
                    <div class="header-main">
                        <div class="title-row">
                            <h3 class="title">群聊成员</h3>
                        </div>
                    </div>
                </div>
            </div>
            
            <div v-show="!group.quit" class="search-section">
                <el-input
                    class="member-search"
                    placeholder="搜索成员昵称或账号"
                    v-model="searchText"
                    size="small"
                    clearable>
                    <i class="el-icon-search el-input__icon" slot="prefix"></i>
                </el-input>
            </div>
            
            <div class="member-list">
                <empty-state
                    v-if="showMembers.length === 0"
                    class="member-empty-state"
                    icon="el-icon-search"
                    :title="currentEmptyText"
                    compact
                ></empty-state>
                <virtual-scroller
                    v-else
                    ref="memberScroller"
                    class="scroll-box"
                    :items="showMembers"
                    :item-height="memberItemHeight"
                    :item-key-getter="getMemberRowKey"
                    @reach-bottom="onMemberReachBottom"
                >
                    <template v-slot="{ item }">
                        <group-member-item
                            :member="item"
                            :group="group"
                            :groupMembers="groupMembers"
                            :height="memberItemHeight"
                            @click.native="onShowUserInfo($event, item)">
                        </group-member-item>
                    </template>
                </virtual-scroller>
            </div>
        </div>
    </div>
</template>
<script>
import VirtualScroller from '../common/VirtualScroller.vue';
import GroupMemberItem from '../group/GroupMemberItem.vue';
import {
    attachContactSearchText,
    CONTACT_SEARCH_TEXT_KEY
} from '../../utils/contactSearchMatcher';

const MEMBER_SEARCH_DEBOUNCE_MS = 250;

export default {
    name: "chatGroupMember",
    components: { VirtualScroller, GroupMemberItem },
    props: {
        group: {
            type: Object
        },
        groupId: {
            type: [Number, String],
            default: null
        },
        groupMembers: {
            type: Array
        }
    },
    data() {
        return {
            searchText: "",
            autoPaging: false,
            memberListVersion: 0,
            memberSearchState: {
                keyword: "",
                items: [],
                cursor: "",
                more: false,
                total: 0,
                loading: false,
                error: false,
                seq: 0
            },
            searchTimer: null
        }
    },
    methods: {
        getScrollerContainer() {
            const scroller = this.$refs.memberScroller;
            return scroller && scroller.$refs ? scroller.$refs.container : null;
        },
        getMemberRowKey(member) {
            return member && member.userId !== undefined && member.userId !== null
                ? `member:${member.userId}`
                : '';
        },
        onBack() {
            this.$emit('back')
        },
        onMemberReachBottom() {
            if (this.normalizedSearchText) {
                this.loadSearchMembers();
                return;
            }
            this.tryLoadMoreMembers();
        },
        resetSearchState(keyword = "") {
            this.memberSearchState.keyword = keyword;
            this.memberSearchState.items = [];
            this.memberSearchState.cursor = "";
            this.memberSearchState.more = false;
            this.memberSearchState.total = 0;
            this.memberSearchState.loading = false;
            this.memberSearchState.error = false;
        },
        normalizeSearchMember(member) {
            if (!member || member.userId === undefined || member.userId === null) {
                return null;
            }
            return this.groupStore.normalizeGroupMember({ ...member });
        },
        mergeSearchMembers(sourceMembers, nextMembers) {
            const merged = [];
            const seen = new Set();
            (Array.isArray(sourceMembers) ? sourceMembers : []).concat(nextMembers || []).forEach((member) => {
                const normalizedMember = this.normalizeSearchMember(member);
                if (!normalizedMember || normalizedMember.quit) {
                    return;
                }
                const key = String(normalizedMember.userId);
                if (seen.has(key)) {
                    return;
                }
                seen.add(key);
                merged.push(normalizedMember);
            });
            return merged;
        },
        loadSearchMembers(options = {}) {
            const keyword = this.normalizedSearchText;
            const reset = options.reset === true;
            if (!this.effectiveGroupId || !keyword) {
                this.resetSearchState("");
                return Promise.resolve(false);
            }
            if (!reset && (this.memberSearchState.loading || !this.memberSearchState.more || !this.memberSearchState.cursor)) {
                return Promise.resolve(false);
            }
            const seq = this.memberSearchState.seq + 1;
            this.memberSearchState.seq = seq;
            if (reset) {
                this.resetSearchState(keyword);
            }
            this.memberSearchState.keyword = keyword;
            this.memberSearchState.loading = true;
            this.memberSearchState.error = false;
            return this.groupStore.fetchGroupMemberSyncPage(this.effectiveGroupId, 0, {
                cursor: reset ? "" : this.memberSearchState.cursor,
                limit: 30,
                maxPages: 1,
                keyword
            }).then((page) => {
                if (seq !== this.memberSearchState.seq || keyword !== this.normalizedSearchText) {
                    return false;
                }
                const nextMembers = Array.isArray(page.items) ? page.items : [];
                this.memberSearchState.items = reset
                    ? this.mergeSearchMembers([], nextMembers)
                    : this.mergeSearchMembers(this.memberSearchState.items, nextMembers);
                this.memberSearchState.cursor = page.cursor || "";
                this.memberSearchState.more = Boolean(page.more && page.cursor);
                this.memberSearchState.total = Math.max(0, Math.floor(Number(page.total) || 0));
                return true;
            }).catch((error) => {
                console.warn('搜索群成员失败', error);
                if (seq === this.memberSearchState.seq) {
                    this.memberSearchState.error = true;
                }
                return false;
            }).finally(() => {
                if (seq === this.memberSearchState.seq) {
                    this.memberSearchState.loading = false;
                }
            });
        },
        scheduleSearchMembers() {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
                this.searchTimer = null;
            }
            const keyword = this.normalizedSearchText;
            this.memberSearchState.seq += 1;
            if (!keyword) {
                this.resetSearchState("");
                this.ensureScrollablePaging();
                return;
            }
            this.resetSearchState(keyword);
            this.memberSearchState.loading = true;
            this.searchTimer = setTimeout(() => {
                this.searchTimer = null;
                this.loadSearchMembers({ reset: true });
            }, MEMBER_SEARCH_DEBOUNCE_MS);
        },
        tryLoadMoreMembers(options = {}) {
            if (!this.effectiveGroupId) {
                return Promise.resolve(false);
            }
            if (this.normalizedSearchText) {
                return Promise.resolve(false);
            }
            if (!this.memberPage.more || this.memberPage.loading || this.autoPaging) {
                return Promise.resolve(false);
            }
            this.autoPaging = true;
            return this.groupStore.loadMoreMembersFromCursor(this.effectiveGroupId, {
                maxPages: 1
            }).then(() => {
                this.memberListVersion += 1;
                return true;
            }).catch(() => false).finally(() => {
                this.autoPaging = false;
            });
        },
        ensureScrollablePaging() {
            if (!this.effectiveGroupId) {
                return;
            }
            if (this.normalizedSearchText) {
                this.ensureScrollableSearchPaging();
                return;
            }
            if (!this.memberPage.more || this.memberPage.loading || this.autoPaging) {
                return;
            }
            this.$nextTick(() => {
                const container = this.getScrollerContainer();
                if (!container) {
                    return;
                }
                if (container.scrollHeight > container.clientHeight + 8) {
                    return;
                }
                this.tryLoadMoreMembers({ fromAutoCheck: true }).then((loaded) => {
                    if (loaded) {
                        this.ensureScrollablePaging();
                    }
                });
            });
        },
        ensureScrollableSearchPaging() {
            if (!this.normalizedSearchText || this.memberSearchState.loading || !this.memberSearchState.more || !this.memberSearchState.cursor) {
                return;
            }
            this.$nextTick(() => {
                const container = this.getScrollerContainer();
                if (!container) {
                    return;
                }
                if (container.scrollHeight > container.clientHeight + 8) {
                    return;
                }
                this.loadSearchMembers().then((loaded) => {
                    if (loaded) {
                        this.ensureScrollableSearchPaging();
                    }
                });
            });
        },
        onShowUserInfo(e, member) {
            if (!this.canViewMemberProfile) {
                this.$message.warning('群资料已设置为不可查看成员资料');
                return;
            }
            this.$http({
                url: `/user/find/${member.userId}`,
                method: 'get'
            }).then(user => {
                let pos = {
                    x: e.x + 30,
                    y: e.y
                }
                this.$eventBus.$emit("openUserInfo", user, pos);
            })
        }
    },
    computed: {
        normalizedSearchText() {
            return String(this.searchText || '').trim().toLowerCase();
        },
        effectiveGroupId() {
            return this.groupId || (this.group && this.group.id) || null;
        },
        storeGroup() {
            return this.effectiveGroupId ? this.groupStore.findGroup(this.effectiveGroupId) : null;
        },
        resolvedGroupMembers() {
            void this.memberListVersion;
            if (this.storeGroup && Array.isArray(this.storeGroup.members)) {
                return this.storeGroup.members;
            }
            return Array.isArray(this.groupMembers) ? this.groupMembers : [];
        },
        canViewMemberProfile() {
            if (!this.effectiveGroupId) {
                return true;
            }
            return this.$permission.hasGroupPermission(this.effectiveGroupId, 'isAllowViewMemberProfile');
        },
        canViewOnlineCount() {
            if (!this.effectiveGroupId) {
                return false;
            }
            return this.$permission.hasGroupPermission(this.effectiveGroupId, 'isAllowViewOnlineCount');
        },
        memberCount() {
            if (Number.isFinite(Number(this.group && this.group.memberCount))) {
                return Number(this.group.memberCount);
            }
            return this.activeMemberRows.length;
        },
        onlineCount() {
            const group = this.storeGroup;
            if (group && typeof group.onlineCount === 'number') {
                return group.onlineCount;
            }
            return this.activeMemberRows.filter((row) => row.member.online).length;
        },
        memberCountText() {
            if (this.canViewOnlineCount) {
                return `在线 ${this.onlineCount} 人`;
            }
            return `共 ${this.memberCount} 人`;
        },
        memberPage() {
            if (!this.effectiveGroupId) {
                return {
                    more: false,
                    cursor: '',
                    total: 0,
                    loading: false
                };
            }
            return this.groupStore.getMemberPageState(this.effectiveGroupId);
        },
        currentEmptyText() {
            if (this.normalizedSearchText) {
                if (this.memberSearchState.loading) {
                    return '正在搜索成员';
                }
                if (this.memberSearchState.error) {
                    return '搜索成员失败';
                }
                return '没有匹配的成员';
            }
            return '暂无可展示成员';
        },
        memberItemHeight() {
            return 64;
        },
        activeMemberRows() {
            return this.resolvedGroupMembers
                .filter((m) => m && !m.quit)
                .map((member) => ({
                    member,
                    searchText: attachContactSearchText(member)[CONTACT_SEARCH_TEXT_KEY]
                }));
        },
        showMembers() {
            const keyword = this.normalizedSearchText;
            if (!keyword) {
                return this.activeMemberRows.map((row) => row.member);
            }
            return this.memberSearchState.keyword === keyword
                ? this.memberSearchState.items
                : [];
        }
    },
    watch: {
        searchText() {
            this.scheduleSearchMembers();
        },
        showMembers() {
            this.ensureScrollablePaging();
        },
        memberPage: {
            deep: true,
            handler() {
                this.ensureScrollablePaging();
            }
        }
    },
    mounted() {
        this.ensureScrollablePaging();
    },
    beforeDestroy() {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }
    }
}
</script>
<style lang="scss" scoped>
@import "../../assets/style/responsive";

.chat-group-member {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--im-chat-bg);

    .member-card-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
        background: var(--im-chat-bg);
        border-radius: 0;
        border: none;
        box-shadow: none;
        box-sizing: border-box;
    }

    .header {
        padding: 0;
        min-height: 60px;
        border-bottom: 1px solid var(--im-surface-panel-border);
        background: var(--im-chat-bg);

        .header-content {
            display: flex;
            align-items: center;
            gap: 10px;
            min-height: inherit;
            padding: 0 12px;

            .back-btn {
                font-size: 16px;
                color: var(--im-text-color);
                cursor: pointer;
                width: 32px;
                height: 32px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                border-radius: 8px;
                background: transparent;
                transition: all 0.2s ease;

                &:hover {
                    background: rgba(var(--im-black-rgb), 0.04);
                }
            }

            .header-main {
                flex: 1;
                min-width: 0;
                text-align: left;
            }

            .title-row {
                display: flex;
                align-items: center;
            }

            .title {
                margin: 0;
                font-size: 16px;
                font-weight: 600;
                color: var(--im-text-color);
                line-height: 1;
            }
        }
    }

    .search-section {
        padding: 10px 12px;
        border-bottom: 1px solid var(--im-surface-panel-border);
    }

    .member-search {
        :deep(.el-input__inner) {
            height: 38px;
            line-height: 38px;
            border-radius: 8px;
            border: 1px solid var(--im-control-border);
            background: var(--im-control-soft-bg);
            padding-left: 38px;
            transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        :deep(.el-input__inner:focus) {
            border-color: rgba(var(--im-primary-rgb), 0.36);
            box-shadow: 0 0 0 3px rgba(var(--im-primary-rgb), 0.1);
        }

        :deep(.el-input__prefix) {
            display: flex;
            align-items: center;
            color: var(--im-text-color-light);
        }

        :deep(.el-input__icon) {
            width: 22px;
            height: 22px;
            line-height: 1;
            border-radius: 6px;
            background: transparent;
        }
    }

    .member-list {
        flex: 1;
        padding: 8px 10px 12px;
        overflow: hidden;

        .scroll-box {
            height: 100%;
        }
    }

    .member-empty-state {
        height: 100%;
    }
}

@include mobile {
    .chat-group-member {
        .member-card-container {
            border-radius: 14px;
        }

        .header,
        .search-section {
            padding-left: 12px;
            padding-right: 12px;
        }

        .summary-row {
            gap: 6px;
        }
    }
}
</style>
